import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Toast Component
 * Notification messages with auto-dismiss and variants
 */
import { useEffect, useState, useCallback, useRef, createContext, useContext } from 'react';
import { cn } from '../utils/cn.js';
const variantStyles = {
    success: {
        container: 'bg-gray-50 border-gray-200 text-gray-800 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100',
        icon: 'text-gray-400 dark:text-green-400',
    },
    error: {
        container: 'bg-gray-50 border-gray-200 text-gray-900 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100',
        icon: 'text-gray-400 dark:text-red-400',
    },
    warning: {
        container: 'bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100',
        icon: 'text-yellow-400 dark:text-yellow-400',
    },
    info: {
        container: 'bg-gray-50 border-gray-200 text-gray-800 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100',
        icon: 'text-gray-400 dark:text-blue-400',
    },
};
const icons = {
    success: (_jsx("svg", { className: "h-5 w-5", fill: "currentColor", viewBox: "0 0 20 20", children: _jsx("path", { fillRule: "evenodd", d: "M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z", clipRule: "evenodd" }) })),
    error: (_jsx("svg", { className: "h-5 w-5", fill: "currentColor", viewBox: "0 0 20 20", children: _jsx("path", { fillRule: "evenodd", d: "M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z", clipRule: "evenodd" }) })),
    warning: (_jsx("svg", { className: "h-5 w-5", fill: "currentColor", viewBox: "0 0 20 20", children: _jsx("path", { fillRule: "evenodd", d: "M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z", clipRule: "evenodd" }) })),
    info: (_jsx("svg", { className: "h-5 w-5", fill: "currentColor", viewBox: "0 0 20 20", children: _jsx("path", { fillRule: "evenodd", d: "M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z", clipRule: "evenodd" }) })),
};
/**
 * Single Toast Item
 */
export function ToastItem({ id, message, variant = 'info', duration = 5000, onClose, action, }) {
    const [isExiting, setIsExiting] = useState(false);
    const exitTimerRef = useRef(null);
    useEffect(() => {
        return () => {
            if (exitTimerRef.current)
                clearTimeout(exitTimerRef.current);
        };
    }, []);
    useEffect(() => {
        if (duration <= 0)
            return;
        const timer = setTimeout(() => {
            setIsExiting(true);
            exitTimerRef.current = setTimeout(() => onClose(id), 200);
        }, duration);
        return () => clearTimeout(timer);
    }, [id, duration, onClose]);
    const handleClose = () => {
        setIsExiting(true);
        exitTimerRef.current = setTimeout(() => onClose(id), 200);
    };
    const styles = variantStyles[variant];
    return (_jsxs("div", { className: cn('flex items-start gap-3 w-full max-w-sm p-4 border rounded-lg shadow-lg', 'transition-all duration-200', isExiting ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0', styles.container), role: "alert", "aria-live": "polite", children: [_jsx("div", { className: styles.icon, children: icons[variant] }), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsx("p", { className: "text-sm font-medium", children: message }), action && (_jsx("button", { type: "button", onClick: action.onClick, className: "mt-2 text-sm font-medium underline hover:no-underline", children: action.label }))] }), _jsx("button", { type: "button", onClick: handleClose, className: cn('flex-shrink-0 rounded p-1 hover:bg-black/5 focus:outline-none focus:ring-2', styles.icon), "aria-label": "Close notification", children: _jsx("svg", { className: "h-4 w-4", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M6 18L18 6M6 6l12 12" }) }) })] }));
}
const positionStyles = {
    'top-left': 'top-4 left-4',
    'top-center': 'top-4 left-1/2 -translate-x-1/2',
    'top-right': 'top-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
    'bottom-right': 'bottom-4 right-4',
};
/**
 * Toast Container - Renders all toasts
 */
export function ToastContainer({ position = 'top-right', maxToasts = 5, }) {
    const { toasts, removeToast } = useToast();
    const visibleToasts = toasts.slice(0, maxToasts);
    if (visibleToasts.length === 0) {
        return null;
    }
    return (_jsx("div", { className: cn('fixed z-50 flex flex-col gap-2', positionStyles[position]), "aria-label": "Notifications", children: visibleToasts.map((toast) => (_jsx(ToastItem, { id: toast.id, message: toast.message, variant: toast.variant, duration: toast.duration, onClose: removeToast, action: toast.action }, toast.id))) }));
}
let toastCounter = 0;
const ToastContext = createContext(null);
export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);
    const addToast = useCallback((toast) => {
        const id = `toast-${++toastCounter}`;
        setToasts((prev) => [...prev, { ...toast, id }]);
        return id;
    }, []);
    const removeToast = useCallback((id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);
    return (_jsx(ToastContext.Provider, { value: { toasts, addToast, removeToast }, children: children }));
}
export function useToast() {
    const ctx = useContext(ToastContext);
    if (!ctx)
        throw new Error('useToast must be used within a ToastProvider');
    return ctx;
}
export function useToastActions() {
    const { addToast } = useToast();
    return {
        success: (message) => addToast({ message, variant: 'success' }),
        error: (message) => addToast({ message, variant: 'error' }),
        warning: (message) => addToast({ message, variant: 'warning' }),
        info: (message) => addToast({ message, variant: 'info' }),
    };
}
//# sourceMappingURL=Toast.js.map